import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { setDraftCount, setHasPublishedPostings } from '~/actions/PermanentJobs/PermanentJobs';
import { OutlineWarning01Icon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Button, Column, Row } from '@tempmee/react';
import { CenteredSpinner, ConfirmPopup } from '~/commonComponents';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import { useGetPostingPlans } from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog';
import { useAppSelector, useNavigationDetector, useToggle } from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';

import { routes } from '~/routes';
import {
  CreateOnsiteInterviewSteps,
  TCreateOnsiteInterviewPageLocationState,
} from '../CreateOnsiteInterview';
import { TermsConditionAddendumPopup } from '../components';
import {
  TCreatePermanentJobPostingPageLocationState,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import { HiringPlanCard, PaymentDetailsPreview } from './components';
import { useDraftJobPost, useRequireTnCAddendum } from './hooks';
import { usePublishJobPost } from './hooks/usePublishJobPost';
import { firePermPostJobSubmitted, firePermPostJobAddInterviewsClicked } from '~/posthogs/events';

/**
 * Helper function to check if a navigation path belongs to the allowed flow routes
 */
const isPathInAllowedFlow = (pathname: string, currentId: string | undefined) => {
  if (!pathname || !currentId) return false;

  // Allow navigation to any path that starts with /permanent-jobs/postings/:id/create
  const allowedPattern = new RegExp(`^/permanent-jobs/postings/${currentId}/create`);

  return allowedPattern.test(pathname);
};

export const ReviewAndPaymentPage = () => {
  const dispatch = useDispatch();
  const draftCount = useAppSelector((state) => state.permanentJobs.draftCount);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();

  // Entered from the Copy & Paste import flow's Confirm Job Details page — "Back"
  // should return there without the exit-confirmation prompt.
  const isFromImport = Boolean(
    (location.state as TCreatePermanentJobPostingPageLocationState | null)?.fromImport,
  );

  const [isPublishSucceed, controlsPublishSucceed] = useToggle();
  const [isShowExitModalConfirmation, controlsExitModalConfirmation] = useToggle();
  const [isShowPaymentError, controlsShowPaymentError] = useToggle();
  const [isShowTnCAddendumPopup, controlsShowTnCAddendumPopup] = useToggle();
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | undefined>();
  const [expectedFinalPrice, setExpectedFinalPrice] = useState<number | undefined>();

  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);

  // Navigation blocking logic
  const { allowNavigation, clearPendingNavigation, navigateToPending } = useNavigationDetector({
    when: true, // Always block navigation from this page
    onNavigationAttempt: (targetLocation) => {
      const targetPath = targetLocation?.pathname;

      // Allow navigation within the create flow, or back to Confirm Details when this
      // page was entered from the import flow — neither needs the exit confirmation.
      const isBackToImportConfirm =
        isFromImport && Boolean(targetPath?.endsWith('/confirm-job-details'));

      if (isPathInAllowedFlow(targetPath, id) || isBackToImportConfirm) {
        navigateToPending();
      } else {
        // Show confirmation modal for navigation outside the flow
        controlsExitModalConfirmation.setOn();
      }
    },
  });

  const { draft, isLoading } = useDraftJobPost(id);
  const {
    data: postingPlans,
    isLoading: isLoadingPlans,
    isError: isErrorPlans,
    error: errorPlans,
  } = useGetPostingPlans();

  // Select the first plan by default
  const selectedPlan = postingPlans?.[0];

  const {
    doPublish,
    isLoading: isPublishing,
    isError: isPublishError,
    error: publishError,
  } = usePublishJobPost({
    jobPostingId: id,
    planId: selectedPlan?.id,
    promoCode: appliedPromoCode,
    expectedFinalPrice,
    onSuccess: () => {
      // Track the job submission event
      if (id && draft?.professionType && selectedPlan?.finalPrice) {
        firePermPostJobSubmitted({
          postingId: id,
          professionType: draft.professionType,
          listingFee: selectedPlan.finalPrice.toFixed(2), // Format as "450.00"
        });
      }

      dispatch(setDraftCount(Math.max(0, (draftCount ?? 1) - 1)));
      dispatch(setHasPublishedPostings(true));
      controlsShowTnCAddendumPopup.setOff();
      controlsPublishSucceed.setOn();
    },
  });

  const { isAcceptingConsent, isConsentAccepted, doAcceptOfficeTnCPerm } = useRequireTnCAddendum({
    controls: controlsShowTnCAddendumPopup,
    onSucceedAcceptConsent: doPublish,
    jobPostingId: id,
  });

  useEffect(() => {
    if (isErrorPlans) {
      showErrorMessage({ error: errorPlans });
    }
  }, [isErrorPlans, errorPlans]);

  useEffect(() => {
    if (isPublishError) {
      if ((publishError as AxiosError<any>)?.response?.data?.errorCode === 'PAYMENT_FAILED') {
        controlsShowPaymentError.setOn();

        return;
      }

      showErrorMessage({ error: publishError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishError, isPublishError]);

  const handleCloseSuccessPopup = () => {
    // Track Add Interviews clicked event
    if (id) {
      firePermPostJobAddInterviewsClicked({ postingId: id });
    }

    controlsPublishSucceed.setOff();
    allowNavigation();
    navigate(
      // eslint-disable-next-line max-len
      `/permanent-jobs/postings/${id}/onsite-interviews/create/${CreateOnsiteInterviewSteps.WorkDetails}`,
      {
        state: {
          isAfterJobPosting: true,
        } satisfies TCreateOnsiteInterviewPageLocationState,
      },
    );
  };

  const handlePromoApplied = (code: string, finalPrice: number) => {
    setAppliedPromoCode(code);
    setExpectedFinalPrice(finalPrice);
  };

  const handlePromoRemoved = () => {
    setAppliedPromoCode(undefined);
    setExpectedFinalPrice(undefined);
  };

  const handlePostJobClicked = () => {
    if (!isConsentAccepted) {
      controlsShowTnCAddendumPopup.setOn();
      return;
    }

    doPublish();
  };

  if (isLoading || !draft || isLoadingPlans) {
    return <CenteredSpinner />;
  }

  const popups = (
    <>
      {isPublishSucceed && (
        <ConfirmPopup
          Icon={() => <AnimatedCheckCircle />}
          rightButtonAction={handleCloseSuccessPopup}
          rightButtonText="Set Up Interviews"
          rightButtonStyle={{ width: 296 }}
          title="Your Role Is Posted!"
          titleStyle={{ marginBottom: 10, marginTop: 14 }}
          description="Professionals are notified and can now see your job posting. Add interviews so they can start applying."
          descriptionStyle={{ marginBottom: 38, marginInline: 30, width: '90%' }}
        />
      )}

      {isShowExitModalConfirmation && (
        <ConfirmPopup
          Icon={() => <OutlineWarning01Icon size={80} color={colors.warning[500]} />}
          leftButtonAction={() => {
            clearPendingNavigation();
            controlsExitModalConfirmation.setOff();
          }}
          leftButtonText="Go Back"
          rightButtonAction={navigateToPending}
          rightButtonText="Leave Anyway"
          title="Are You Sure You Want to Leave?"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          descriptionStyle={{ marginBottom: 40 }}
          description="Your job isn't posted yet, but we saved it as a draft under Manage Jobs. Do you still want to leave this page?"
        />
      )}

      {isShowPaymentError && (
        <ConfirmPopup
          Icon={() => <OutlineWarning01Icon color={colors.warning[500]} />}
          leftButtonStyle={{ width: 172 }}
          leftButtonAction={() => controlsShowPaymentError.setOff()}
          leftButtonText="Go Back"
          rightButtonStyle={{ width: 172 }}
          rightButtonAction={() => {
            allowNavigation();
            navigate(routes.paymentInformation);
          }}
          rightButtonText="Update Payment"
          title="Payment Failed - Job Not Posted"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          descriptionStyle={{ marginBottom: 40 }}
          description="We couldn't process your payment. Your job has been saved in your drafts. Please update your payment method and try posting again."
        />
      )}

      <TermsConditionAddendumPopup
        isLoading={isAcceptingConsent || isPublishing}
        isOpen={isShowTnCAddendumPopup}
        onConfirm={doAcceptOfficeTnCPerm}
        jobPostingId={id}
      />
    </>
  );

  if (isEnforcePostAJobFunnelImprovement) {
    return (
      <>
        <Column gap={3} width="892px" className="self-center">
          <HiringPlanCard
            plan={selectedPlan}
            discountedPrice={expectedFinalPrice}
            onPromoApply={handlePromoApplied}
            onPromoRemove={handlePromoRemoved}
          />

          <Row justify="center" className="mt-2">
            <Button
              onClick={handlePostJobClicked}
              isLoading={isAcceptingConsent || isPublishing}
              size="lg"
            >
              Post My Job
            </Button>
          </Row>
        </Column>

        {popups}
      </>
    );
  }

  return (
    <>
      <Column gap={2} width="892px" className="self-center">
        <PaymentDetailsPreview
          plan={selectedPlan}
          discountedPrice={expectedFinalPrice}
          onPromoApply={handlePromoApplied}
          onPromoRemove={handlePromoRemoved}
        />
        <Row justify="center" className="mt-2">
          <Button
            onClick={handlePostJobClicked}
            isLoading={isAcceptingConsent || isPublishing}
            size="lg"
          >
            Post Job
          </Button>
        </Row>
      </Column>

      {popups}
    </>
  );
};
